/**
 * M1-9: User Actions Pill — bottom-left of the HUD (vertical pill).
 *
 * Buttons (top → bottom):
 *   1. Feedback — opens feedback modal (stub: M1-9 full impl pending API)
 *   2. Points/Credits — shows credit balance
 *   3. User avatar — shows user info popover (name + phone)
 *
 * Built on @heroui/react Button + @gravity-ui/icons.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Xmark, Person, ArrowRightFromSquare } from '@gravity-ui/icons';
import { IcQuestion, IcCredit } from './HudIcons';
import { Avatar, Button, ButtonGroup } from '@heroui/react';
import { useAuthStore } from '@stores/authStore';
import { fetchCreditBalance, type CreditBalance } from '@services/user';
import { authAdapter } from '@services/auth';
import { isParentBridgeAvailable, requestParentFeedback } from '@services/parentBridge';
import { useViewerStore } from '@stores/viewerStore';
import { Pill } from './PillPrimitives';
import { MtTooltip } from '@components/MtTooltip';
import { formatBalance } from '@utils/format';

// ---------------------------------------------------------------------------
// Feedback modal stub
// ---------------------------------------------------------------------------

function FeedbackModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <>
      <button
        type="button"
        className="absolute inset-0 z-40 cursor-default appearance-none border-none bg-transparent p-0"
        aria-label="Close feedback"
        onClick={onClose}
      />
      <div className="absolute bottom-0 left-14 z-50 w-72 max-w-[calc(100vw-7rem)] rounded-2xl bg-white/10 p-5 shadow-xl backdrop-blur-md">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-white">{t('user.feedbackTitle')}</h3>
          <Button
            isIconOnly
            size="sm"
            onPress={onClose}
            aria-label="Close"
            className="text-white/40 hover:text-white"
          >
            <Xmark width={14} height={14} />
          </Button>
        </div>
        <textarea
          rows={4}
          aria-label="Feedback message"
          placeholder={t('user.feedbackPlaceholder')}
          className="w-full resize-none rounded-xl bg-white/10 p-3 text-sm text-white placeholder-white/30 outline-none focus:ring-1 focus:ring-white/30"
        />
        {/* TODO(M1-9): wire to ticket system API when endpoint is confirmed */}
        <Button
          isDisabled
          fullWidth
          size="sm"
          className="mt-3 rounded-full bg-white/20 text-sm font-medium text-white/50"
        >
          {t('user.feedbackSubmit')}
        </Button>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// 积分余额 hook — 供 tooltip 显示
// ---------------------------------------------------------------------------

function useCreditBalance() {
  const [credit, setCredit] = useState<CreditBalance | null>(null);
  const isLoggedIn = useAuthStore((s) => s.isLoggedIn);

  useEffect(() => {
    if (!isLoggedIn) {
      setCredit(null);
      return;
    }
    const ctrl = new AbortController();
    void fetchCreditBalance(ctrl.signal)
      .then(setCredit)
      .catch(() => {});
    return () => ctrl.abort();
  }, [isLoggedIn]);

  return credit;
}

// ---------------------------------------------------------------------------
// User info popover — 显示名称和手机号
// ---------------------------------------------------------------------------

interface UserInfoPopoverProps {
  onClose: () => void;
  panelClassName?: string;
}

export function UserInfoPopover({
  onClose,
  panelClassName = 'absolute bottom-0 left-14 z-50 w-60 max-w-[calc(100vw-7rem)] rounded-2xl bg-white/10 p-5 shadow-xl backdrop-blur-md',
}: UserInfoPopoverProps) {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);

  return (
    <>
      <button
        type="button"
        className="absolute inset-0 z-40 cursor-default appearance-none border-none bg-transparent p-0"
        aria-label="Close user info"
        onClick={onClose}
      />
      <div className={panelClassName}>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-white">{t('user.userInfo')}</h3>
          <Button
            isIconOnly
            size="sm"
            onPress={onClose}
            aria-label="Close"
            className="text-white/40 hover:text-white"
          >
            <Xmark width={14} height={14} />
          </Button>
        </div>
        {user ? (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              {user.avatar ? (
                <Avatar className="h-9 w-9 shrink-0 rounded-full">
                  <Avatar.Image alt={user.name} src={user.avatar} />
                </Avatar>
              ) : (
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/20">
                  <Person width={16} height={16} className="text-white/70" />
                </div>
              )}
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-white">{user.name}</div>
                {user.phone && <div className="truncate text-xs text-white/50">{user.phone}</div>}
              </div>
            </div>
            <Button
              size="sm"
              fullWidth
              onPress={() => {
                void authAdapter.logout();
                onClose();
              }}
              className="rounded-full bg-white/10 text-xs text-white/70 hover:bg-white/20 hover:text-white"
            >
              <ArrowRightFromSquare width={12} height={12} />
              {t('user.logout')}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-sm text-white/50">{t('user.notLoggedIn')}</div>
            <Button
              size="sm"
              fullWidth
              onPress={() => {
                void authAdapter.login();
                onClose();
              }}
              className="rounded-full bg-white/20 text-xs font-medium text-white hover:bg-white/30"
            >
              {t('user.login')}
            </Button>
          </div>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// UserPill
// ---------------------------------------------------------------------------

export function UserPill() {
  const { t } = useTranslation();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [userInfoOpen, setUserInfoOpen] = useState(false);
  const credit = useCreditBalance();
  const user = useAuthStore((s) => s.user);
  const isLoggedIn = useAuthStore((s) => s.isLoggedIn);
  const sceneId = useViewerStore((s) => s.sceneId);
  const taskData = useViewerStore((s) => s.taskData);

  const handleFeedback = () => {
    if (isParentBridgeAvailable()) {
      const content = [
        `Task ID: ${taskData?.id ?? '-'}`,
        `Task Code: ${taskData?.task_code ?? sceneId ?? '-'}`,
        `Task Name: ${taskData?.task_name ?? '-'}`,
        '',
      ].join('\n');
      const posted = requestParentFeedback({
        feedback: {
          feedbackType: 'experience',
          title: taskData?.task_name
            ? `Explorer feedback: ${taskData.task_name}`
            : 'Explorer feedback',
          content,
        },
      });
      if (posted) return;
    }
    setFeedbackOpen(true);
  };

  return (
    <>
      <Pill horizontal={false} className="gap-2 py-1">
        <ButtonGroup
          className="flex-col gap-0"
          size="md"
          orientation="vertical"
          variant="secondary"
        >
          {/* Feedback */}
          <Button
            isIconOnly
            aria-label="Send feedback"
            onPress={handleFeedback}
            className="rounded-t-full"
          >
            <IcQuestion className="size-4" />
          </Button>

          {/* Points/Credits — tooltip 展示余额或未登录提示，点击未登录时触发登录 */}
          <MtTooltip
            placement="right"
            content={
              isLoggedIn
                ? credit
                  ? `${formatBalance(credit.balance)} ${t('user.pointsAvailable')}`
                  : t('user.pointsLoading')
                : t('user.notLoggedIn')
            }
            mobileContent={(close) => (
              <button
                type="button"
                className="px-2 py-1 text-sm"
                onClick={close}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    close();
                  }
                }}
              >
                {isLoggedIn
                  ? credit
                    ? `${formatBalance(credit.balance)} ${t('user.pointsAvailable')}`
                    : t('user.pointsLoading')
                  : t('user.notLoggedIn')}
              </button>
            )}
          >
            <Button
              size="md"
              variant="secondary"
              isIconOnly
              aria-label="Points"
              className="rounded-b-full"
              onPress={() => {
                if (!isLoggedIn) void authAdapter.login();
              }}
            >
              <ButtonGroup.Separator />
              <IcCredit className="size-4" />
            </Button>
          </MtTooltip>
        </ButtonGroup>

        <Button
          variant="secondary"
          isIconOnly
          aria-label="User"
          className={'rounded-full'}
          size="md"
          onPress={() => {
            if (!isLoggedIn) {
              void authAdapter.login();
              return;
            }
            setUserInfoOpen(true);
          }}
        >
          <Avatar className="h-full w-full rounded-full" size="md">
            {user?.avatar ? (
              <Avatar.Image alt={user.name} src={user.avatar} />
            ) : (
              <Avatar.Fallback className="h-full w-full rounded-full">
                {user?.name?.charAt(0)?.toUpperCase() ?? '?'}
              </Avatar.Fallback>
            )}
          </Avatar>
        </Button>
      </Pill>

      {feedbackOpen && <FeedbackModal onClose={() => setFeedbackOpen(false)} />}
      {userInfoOpen && <UserInfoPopover onClose={() => setUserInfoOpen(false)} />}
    </>
  );
}
